import {
  DEFAULT_DISCUSSION_DURATION_SECONDS,
  DEFAULT_VOTE_WINDOW_SECONDS,
  type GameState,
  type InitGameOptions,
} from "./types";
import { buildPlayerStates, normalizeSeatOrder } from "./utils/seat-order";

const MIN_PLAYERS = 3;
const MAX_PLAYERS = 10;

function assertPlayerCount(count: number): void {
  if (count < MIN_PLAYERS || count > MAX_PLAYERS) {
    throw new Error("Player count must be between 3 and 10");
  }
}

function assertUniquePlayerIds(players: InitGameOptions["players"]): void {
  const ids = new Set<string>();
  for (const player of players) {
    if (ids.has(player.id)) {
      throw new Error("Player ids must be unique");
    }
    ids.add(player.id);
  }
}

export function initGame(options: InitGameOptions): GameState {
  if (options.seed.trim().length === 0) {
    throw new Error("Seed is required");
  }
  assertPlayerCount(options.players.length);
  assertUniquePlayerIds(options.players);

  const orderedPlayers = normalizeSeatOrder(
    options.players,
    options.hostPlayerId,
  );
  const players = buildPlayerStates(orderedPlayers);

  return {
    gameId: options.gameId,
    seed: options.seed,
    createdAt: options.createdAt,
    version: 0,
    phase: "setup",
    hostPlayerId: options.hostPlayerId,
    players,
    seatOrder: players.map((player) => player.id),
    seatLocked: false,
    selectedRoleIds: [],
    cards: [],
    cardPositions: {},
    centerSlots: [null, null, null],
    privateKnowledge: {},
    doppelgangerCopiedRoleId: null,
    rolePeekConfirmedPlayerIds: [],
    nightRoleQueue: [],
    announcerTimeline: [],
    nightLog: [],
    discussionDurationSeconds: DEFAULT_DISCUSSION_DURATION_SECONDS,
    discussionRemainingSeconds: null,
    voteWindowSeconds: DEFAULT_VOTE_WINDOW_SECONDS,
    voteRemainingSeconds: null,
    autoVotePlayerIds: [],
    voteResult: null,
    revealedCardIds: [],
    gameOutcome: null,
  };
}
